import React, { useEffect, useMemo, useRef, useState } from 'react';

import { lensTokens } from '~/configs/unifiedAnalyticsTheme';

import CloseIcon from '@mui/icons-material/Close';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import PersonRemoveOutlinedIcon from '@mui/icons-material/PersonRemoveOutlined';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Dialog,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import { FONT_STACK_SUISSE_WORKS } from '~/components/brand';

import { useGetReportingLeLens, useSetReportingLeLensStatus } from '~/hooks/apis/reporting/useGetReportingLeLens';
import { useUpdateLensSharing } from '~/hooks/apis/reporting/useLensSharing';
import { useCreateLensSpace } from '~/hooks/apis/reporting/useLensSpaces';

import type { Lens, LensRole, LensShareRecipient, LensSpace } from '~/types/leLens';

import LensRecipientInput from './LensRecipientInput';
import LensShareExport from './LensShareExport';
import { lensAudienceSentence, lensHomeLabel, ownerFirstName } from './lensLibrary';
import { lensShareUrl } from './lensShareContent';
import type { LensUrlState } from './lensUrlState';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Direct shares grant one of two things. Manager is held by the owner and by a space's managers. */
const RECIPIENT_ROLES: Array<{ value: LensRole; label: string }> = [
  { value: 'viewer', label: 'Can view' },
  { value: 'editor', label: 'Can edit' },
];

interface ShareLensDialogProps {
  open: boolean;
  onClose: () => void;
  lens: Lens | null;
  /** The spaces the signed-in employee belongs to, which are the only homes they can move a Lens into. */
  spaces: Array<LensSpace>;
  viewerEmail?: string | null;
  currentView?: LensUrlState;
  spacesLoading?: boolean;
  spacesError?: boolean;
  /** The Lens toolbar embeds these controls in its central Publish dialog. */
  presentation?: 'share' | 'publish';
}

/**
 * One dialog for the whole question of who can see a Lens, disclosed in the order
 * a person decides it: what is true now, then the lightweight one-off share, then
 * a durable team home, then the company promotion.
 *
 * Publishing is not another name in the recipient list. It is a different kind of
 * act with a different blast radius, so it sits last, behind its own confirmation,
 * and is offered only to the reporting admins who may grant it.
 */
export default function ShareLensDialog({
  open,
  onClose,
  lens,
  spaces,
  viewerEmail,
  currentView,
  spacesLoading,
  spacesError,
  presentation = 'share',
}: ShareLensDialogProps) {
  const publishDialog = presentation === 'publish';
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const updateSharing = useUpdateLensSharing();
  const setStatus = useSetReportingLeLensStatus();
  const createSpace = useCreateLensSpace();
  const [creatingSpace, setCreatingSpace] = useState(false);
  const [spaceName, setSpaceName] = useState('');
  const [createdSpace, setCreatedSpace] = useState<LensSpace | null>(null);
  const [spaceError, setSpaceError] = useState<string | null>(null);
  const [includeView, setIncludeView] = useState(true);
  const availableSpaces =
    createdSpace && !spaces.some((space) => space.id === createdSpace.id) ? [...spaces, createdSpace] : spaces;
  const writableSpaces = availableSpaces.filter((space) => space.role === 'editor' || space.role === 'manager');
  /**
   * The recipient list lives on the detail response, not the list one: naming the
   * colleagues a Lens was shared with is only shown to someone who may change it,
   * so it is not carried on every row of a library page.
   */
  const detail = useGetReportingLeLens(open && lens ? lens.id : undefined);
  const recipients = useMemo(() => detail.data?.sharing?.recipients ?? [], [detail.data]);
  const recipientsLoaded = !detail.isLoading && !detail.isError;

  const selectedLensRef = useRef(lens);
  selectedLensRef.current = lens;
  const hydratedLensId = useRef<string | null>(null);
  const [activeLens, setActiveLens] = useState<Lens | null>(null);
  const [draft, setDraft] = useState<Array<LensShareRecipient>>([]);
  const [confirmedRecipients, setConfirmedRecipients] = useState<Array<LensShareRecipient>>([]);
  const [invitee, setInvitee] = useState('');
  const [inviteeRole, setInviteeRole] = useState<LensRole>('viewer');
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [moveTo, setMoveTo] = useState<string>('');
  const [newManager, setNewManager] = useState('');
  const [confirmingPublish, setConfirmingPublish] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);

  const currentLens = activeLens?.id === lens?.id ? activeLens : lens;
  const home = currentLens?.home;
  const currentSpaceId = home?.kind === 'space' ? home.spaceId : '';
  const canShare = !detail.isError && Boolean(detail.data?.access?.canShare ?? currentLens?.access?.canShare);
  const effectiveAccess = detail.data?.access ?? currentLens?.access;
  const canShareEdit = Boolean(effectiveAccess?.canShareEdit ?? effectiveAccess?.canEdit);
  const canManageSharing = Boolean(effectiveAccess?.canManageSharing ?? effectiveAccess?.role === 'manager');
  const allowedRecipientRoles = RECIPIENT_ROLES.filter((role) => canShareEdit || role.value === 'viewer');
  const canPublish = !detail.isError && Boolean(detail.data?.access?.canPublish ?? currentLens?.access?.canPublish);
  const canUnpublish =
    !detail.isError && Boolean(detail.data?.access?.canUnpublish ?? currentLens?.access?.canUnpublish);
  const published = currentLens?.status === 'published';
  const publishedDialog = publishDialog && published;
  const busy = updateSharing.isPending || setStatus.isPending || createSpace.isPending || detail.isLoading;

  useEffect(() => {
    const selectedLens = selectedLensRef.current;
    if (open && selectedLens) {
      hydratedLensId.current = null;
      setActiveLens(selectedLens);
      setDraft([]);
      setConfirmedRecipients([]);
      setInvitee('');
      setInviteeRole('viewer');
      setInviteError(null);
      setMoveTo(selectedLens.home?.kind === 'space' ? selectedLens.home.spaceId : '');
      setNewManager(viewerEmail ?? '');
      setConfirmingPublish(false);
      setCopied(false);
      setCopyError(false);
      setCreatingSpace(false);
      setSpaceName('');
      setCreatedSpace(null);
      setSpaceError(null);
      setIncludeView(true);
    }
  }, [lens?.id, open, viewerEmail]);

  // Hydrate the editable draft once per opening. A refetch after a mutation must
  // not overwrite a role the reader is in the middle of changing.
  useEffect(() => {
    if (!open || !lens || !detail.data || hydratedLensId.current === lens.id) {
      return;
    }
    const detailedLens: Lens = {
      ...detail.data.lens,
      access: { ...lens.access, ...detail.data.access },
      audience: detail.data.sharing?.audience ?? detail.data.lens.audience,
      home: detail.data.sharing?.home ?? detail.data.lens.home,
      recipientCount: detail.data.sharing?.recipientCount ?? detail.data.lens.recipientCount,
    };
    hydratedLensId.current = lens.id;
    setActiveLens(detailedLens);
    setDraft(recipients);
    setConfirmedRecipients(recipients);
    setMoveTo(detailedLens.home?.kind === 'space' ? detailedLens.home.spaceId : '');
  }, [detail.data, lens, open, recipients]);

  const sharedView = includeView ? currentView : undefined;
  const shareUrl = currentLens ? lensShareUrl(window.location.origin, currentLens.id, sharedView) : '';

  /**
   * The copy-link feedback has to say whether the link is enough on its own. A
   * link to a private Lens is a link to a refusal, and a person who was not told
   * that finds out from the colleague they sent it to.
   */
  const linkReach =
    currentLens?.audience === 'reporting-admins'
      ? 'Link copied. The creator and reporting admins can open this Lens; add anyone else below.'
      : published
        ? 'Link copied. Every employee can open this Lens.'
        : confirmedRecipients.length > 0 ||
            currentSpaceId ||
            currentLens?.audience === 'people' ||
            currentLens?.audience === 'team'
          ? 'Link copied. Reporting admins and the people and teams listed here can open it.'
          : 'Link copied. The creator and reporting admins can open this Lens; add anyone else below.';

  if (!currentLens) {
    return null;
  }

  const mergeActiveLens = (next: Lens) => {
    setActiveLens((current) => ({ ...next, access: current?.access ?? lens?.access }));
  };

  const changeStatus = async (status: Lens['status']) => {
    try {
      const result = await setStatus.mutateAsync({ id: currentLens.id, status });
      mergeActiveLens(result.lens);
      setConfirmingPublish(false);
      if (publishDialog) onClose();
    } catch {
      // The mutation reports the error; keep the current audience and confirmation for retry.
    }
  };

  const draftWithInvitee = (): Array<LensShareRecipient> | null => {
    const email = invitee.trim().toLowerCase();
    if (!EMAIL_PATTERN.test(email)) {
      setInviteError('Enter a work email address');
      return null;
    }
    if (!currentSpaceId && email === currentLens.ownerEmail.trim().toLowerCase()) {
      setInviteError('This person already manages the Lens');
      return null;
    }
    if (draft.some((person) => person.email === email)) {
      setInviteError('This person is already on the list');
      return null;
    }
    return [...draft, { email, role: inviteeRole }];
  };

  const addRecipient = () => {
    const next = draftWithInvitee();
    if (!next) return;
    setDraft(next);
    setInvitee('');
    setInviteError(null);
  };

  const saveRecipients = async (next: Array<LensShareRecipient>, successMessage: string) => {
    try {
      const result = await updateSharing.mutateAsync({
        lensId: currentLens.id,
        ...(canManageSharing
          ? { recipients: next.map((person) => ({ email: person.email, role: person.role })) }
          : {
              grants: next
                .filter(
                  (person) =>
                    !confirmedRecipients.some((saved) => saved.email === person.email && saved.role === person.role),
                )
                .map((person) => ({ email: person.email, role: person.role })),
            }),
        successMessage,
      });
      setDraft(result.recipients);
      setConfirmedRecipients(result.recipients);
      mergeActiveLens(result.lens);
    } catch {
      // PUT replaces the full recipient list. Restore the last server-confirmed
      // list so a failed remove or role change is never presented as saved.
      setDraft(confirmedRecipients);
    }
  };

  const moveHome = async () => {
    const leavingSpace = Boolean(currentSpaceId) && !moveTo;
    const result = await updateSharing.mutateAsync({
      lensId: currentLens.id,
      spaceId: moveTo || null,
      ownerEmail: leavingSpace ? newManager.trim().toLowerCase() : undefined,
      successMessage: moveTo
        ? `Moved to ${availableSpaces.find((space) => space.id === moveTo)?.name ?? 'the team space'}`
        : 'Moved back to a personal Lens',
    });
    mergeActiveLens(result.lens);
    setMoveTo(result.lens.home?.kind === 'space' ? result.lens.home.spaceId : '');
  };

  const targetSpace = availableSpaces.find((space) => space.id === moveTo);
  const leavingSpace = Boolean(currentSpaceId) && !moveTo;
  const homeChanged = (moveTo || '') !== (currentSpaceId || '');
  const saveSharing = async () => {
    if (!canShare || busy || !recipientsLoaded) return;
    const next = invitee.trim() ? draftWithInvitee() : draft;
    if (!next) return;
    try {
      const result = await updateSharing.mutateAsync({
        lensId: currentLens.id,
        ...(canManageSharing
          ? { recipients: next.map((person) => ({ email: person.email, role: person.role })) }
          : {
              grants: next
                .filter(
                  (person) =>
                    !confirmedRecipients.some((saved) => saved.email === person.email && saved.role === person.role),
                )
                .map((person) => ({ email: person.email, role: person.role })),
            }),
        ...(canManageSharing && homeChanged
          ? {
              spaceId: moveTo || null,
              ownerEmail: leavingSpace ? newManager.trim().toLowerCase() : undefined,
            }
          : {}),
        successMessage: 'Sharing saved. This Lens is still published.',
      });
      setDraft(result.recipients);
      setConfirmedRecipients(result.recipients);
      mergeActiveLens(result.lens);
      onClose();
    } catch {
      // Keep the unsaved changes available for retry; the mutation reports the error.
    }
  };
  const label = (text: string) => (
    <Typography
      sx={{
        color: tokens.accent,
        fontSize: 11,
        fontWeight: 750,
        letterSpacing: '0.09em',
        mb: 1,
        mt: 3,
        textTransform: 'uppercase',
      }}
    >
      {text}
    </Typography>
  );

  return (
    <Dialog
      PaperProps={{
        sx: {
          bgcolor: tokens.canvas,
          borderRadius: publishDialog ? 1 : 0,
          height: publishDialog ? 'auto' : '100%',
          m: publishDialog ? 2 : 0,
          maxHeight: publishDialog ? 'calc(100% - 32px)' : '100%',
          width: publishDialog ? { xs: 'calc(100% - 32px)', sm: 600 } : { xs: '100%', sm: 440 },
        },
      }}
      onClose={busy ? undefined : onClose}
      open={open}
      maxWidth={false}
      aria-labelledby={publishDialog ? 'lens-publish-title' : 'lens-share-title'}
      sx={{ '& .MuiDialog-container': { justifyContent: publishDialog ? 'center' : 'flex-end' } }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ flexShrink: 0, borderBottom: '1px solid', borderColor: alpha(tokens.accent, 0.16), px: 3, py: 2.5 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            noWrap
            sx={{
              color: tokens.accent,
              fontSize: 11,
              fontWeight: 750,
              letterSpacing: '0.13em',
              textTransform: 'uppercase',
            }}
            title={currentLens.title}
          >
            {currentLens.title}
          </Typography>
          <Typography
            component="h2"
            id={publishDialog ? 'lens-publish-title' : 'lens-share-title'}
            sx={{ color: tokens.accent, fontFamily: FONT_STACK_SUISSE_WORKS, fontSize: 25, fontWeight: 400 }}
          >
            {publishDialog && (published ? canUnpublish : canPublish)
              ? published
                ? `Published “${currentLens.title}”`
                : `Publish “${currentLens.title}”?`
              : 'Share Lens'}
          </Typography>
        </Box>
        <IconButton aria-label="Close" disabled={busy} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Stack>

      <Box sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', px: 3, py: 3 }}>
        {publishDialog && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            {published
              ? 'Save updates sharing while keeping this Lens published. Unpublish removes it from the company library.'
              : 'Choose who can access this Lens below. Publishing to the company makes it available to every employee.'}
          </Typography>
        )}
        <Box sx={{ bgcolor: tokens.accent, borderRadius: '6px', color: tokens.accentContrast, p: 2 }}>
          <Typography sx={{ color: tokens.accentContrast, fontSize: 11, opacity: 0.78 }}>
            Current home and audience
          </Typography>
          <Typography sx={{ color: tokens.accentContrast, fontSize: 15, fontWeight: 650, mt: 0.5 }}>
            {lensHomeLabel(currentLens, viewerEmail)} · {lensAudienceSentence(currentLens.audience, home)}
          </Typography>
        </Box>

        {!canShare && (
          <Alert severity="info" sx={{ mt: 2 }}>
            You do not have permission to share this Lens.
          </Alert>
        )}

        {detail.isError && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            We couldn’t load who this Lens is already shared with, so saving now could remove someone. Close this and
            try again.
          </Alert>
        )}

        <Alert severity="info" sx={{ mt: 2 }}>
          Reporting admins can edit and share any Lens. Viewers can grant view access; editors can grant view or edit
          access. Only managers can remove people or change the team home.
        </Alert>
        {label('Share with people')}
        <Stack direction="row" spacing={1}>
          <LensRecipientInput
            disabled={!open || !canShare || busy}
            value={invitee}
            error={inviteError}
            excludedEmails={[
              ...draft.map((person) => person.email),
              ...(!currentSpaceId ? [currentLens.ownerEmail] : []),
            ]}
            onChange={(value) => {
              setInvitee(value);
              setInviteError(null);
            }}
          />
          <TextField
            disabled={!canShare || busy}
            inputProps={{ 'aria-label': 'Access for the person being added' }}
            onChange={(event) => setInviteeRole(event.target.value as LensRole)}
            select
            size="small"
            sx={{ minWidth: 118 }}
            value={inviteeRole}
          >
            {allowedRecipientRoles.map((role) => (
              <MenuItem key={role.value} value={role.value}>
                {role.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <Button
          disabled={!canShare || busy || !invitee.trim()}
          onClick={addRecipient}
          size="small"
          sx={{ mt: 1 }}
          variant="outlined"
        >
          Add
        </Button>

        <Stack sx={{ mt: 1.5 }}>
          {draft.length === 0 ? (
            <Typography color="text.secondary" sx={{ fontSize: 12.5, py: 1 }}>
              Nobody has been named yet. {ownerFirstName(currentLens.ownerEmail)} manages this Lens.
            </Typography>
          ) : (
            draft.map((person) => (
              <Stack
                alignItems="center"
                direction="row"
                key={person.email}
                spacing={1.25}
                sx={{ borderBottom: '1px solid', borderColor: alpha(tokens.accent, 0.12), py: 1.25 }}
              >
                <Avatar
                  sx={{
                    bgcolor: alpha(tokens.accent, 0.12),
                    color: tokens.accent,
                    fontSize: 11,
                    height: 28,
                    width: 28,
                  }}
                >
                  {person.email.charAt(0).toUpperCase()}
                </Avatar>
                <Typography noWrap sx={{ flex: 1, fontSize: 12.5, minWidth: 0 }} title={person.email}>
                  {person.email}
                </Typography>
                <TextField
                  disabled={!canShare || busy || (!canShareEdit && person.role === 'editor')}
                  inputProps={{ 'aria-label': `Access for ${person.email}` }}
                  onChange={(event) =>
                    setDraft((current) =>
                      current.map((entry) =>
                        entry.email === person.email ? { ...entry, role: event.target.value as LensRole } : entry,
                      ),
                    )
                  }
                  select
                  size="small"
                  sx={{ minWidth: 112, '& .MuiSelect-select': { fontSize: 12 } }}
                  value={person.role}
                >
                  {RECIPIENT_ROLES.map((role) => (
                    <MenuItem
                      key={role.value}
                      disabled={
                        (!canShareEdit && role.value === 'editor') ||
                        (!canManageSharing && person.role === 'editor' && role.value === 'viewer')
                      }
                      sx={{ fontSize: 12.5 }}
                      value={role.value}
                    >
                      {role.label}
                    </MenuItem>
                  ))}
                </TextField>
                <Tooltip title={`Remove ${person.email}`}>
                  <span>
                    <IconButton
                      aria-label={`Remove ${person.email}`}
                      disabled={!canManageSharing || busy || !recipientsLoaded}
                      onClick={() => {
                        const next = draft.filter((entry) => entry.email !== person.email);
                        if (publishedDialog) setDraft(next);
                        else void saveRecipients(next, `${person.email} can no longer open this Lens`);
                      }}
                      size="small"
                    >
                      <PersonRemoveOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            ))
          )}
        </Stack>
        {canShare && !publishedDialog && (
          <Button
            disabled={busy || !recipientsLoaded}
            onClick={() => saveRecipients(draft, 'Who can open this Lens has changed')}
            size="small"
            sx={{ mt: 1.5 }}
            variant="contained"
          >
            {updateSharing.isPending ? 'Saving…' : 'Save people'}
          </Button>
        )}

        {label('Team home')}
        {spacesLoading && <Typography variant="body2">Loading team spaces…</Typography>}
        {spacesError && <Alert severity="warning">We couldn’t load team spaces. Close this panel and try again.</Alert>}
        {availableSpaces.length === 0 ? (
          <Typography color="text.secondary" sx={{ fontSize: 12.5, lineHeight: 1.55 }}>
            You are not in a team space yet. Sharing by name suits one-off work; a team space is where a group returns
            to the same analysis, and everyone in it inherits access.
          </Typography>
        ) : (
          <>
            <TextField
              disabled={!canManageSharing || busy}
              fullWidth
              helperText={
                targetSpace
                  ? `All ${targetSpace.memberCount} members of ${targetSpace.name} will be able to open this Lens. Their space role decides what they can change.`
                  : 'Personal keeps you as manager. Reporting admins and the people named above can open it.'
              }
              label="Home"
              onChange={(event) => setMoveTo(event.target.value)}
              select
              size="small"
              value={moveTo}
            >
              <MenuItem value="">Personal</MenuItem>
              {availableSpaces
                .filter((space) => space.id === currentSpaceId || writableSpaces.includes(space))
                .map((space) => (
                  <MenuItem key={space.id} value={space.id} disabled={!writableSpaces.includes(space)}>
                    {space.name}
                  </MenuItem>
                ))}
            </TextField>
            {leavingSpace && (
              <TextField
                disabled={busy}
                fullWidth
                helperText="A Lens leaving a team space needs a personal manager, or nobody is left who can share or repair it."
                label="New personal manager"
                onChange={(event) => setNewManager(event.target.value)}
                size="small"
                sx={{ mt: 1.5 }}
                value={newManager}
              />
            )}
            {canManageSharing && homeChanged && !publishedDialog && (
              <Button
                disabled={busy || (leavingSpace && !EMAIL_PATTERN.test(newManager.trim()))}
                onClick={() => {
                  void moveHome().catch(() => undefined);
                }}
                size="small"
                sx={{ mt: 1.5 }}
                variant="contained"
              >
                {updateSharing.isPending ? 'Moving…' : targetSpace ? `Move to ${targetSpace.name}` : 'Move to Personal'}
              </Button>
            )}
          </>
        )}

        {canManageSharing && (
          <Box sx={{ mt: 2 }}>
            {creatingSpace ? (
              <Stack spacing={1}>
                <TextField
                  label="New team space name"
                  value={spaceName}
                  onChange={(event) => {
                    setSpaceName(event.target.value);
                    setSpaceError(null);
                  }}
                  disabled={busy}
                  required
                  size="small"
                  error={Boolean(spaceError)}
                  helperText={
                    spaceError ?? 'You will manage the new space. Add colleagues through Manage space in the library.'
                  }
                  inputProps={{ maxLength: 80 }}
                />
                <Stack direction="row" spacing={1}>
                  <Button
                    disabled={busy || !spaceName.trim()}
                    onClick={async () => {
                      setSpaceError(null);
                      try {
                        const result = await createSpace.mutateAsync({ name: spaceName.trim(), description: null });
                        if (!result.space) throw new Error('The server did not return the new team space.');
                        setCreatedSpace(result.space);
                        setMoveTo(result.space.id);
                        setCreatingSpace(false);
                        setSpaceName('');
                      } catch (error) {
                        setSpaceError(error instanceof Error ? error.message : 'Could not create the team space.');
                      }
                    }}
                  >
                    {createSpace.isPending ? 'Creating…' : 'Create team space'}
                  </Button>
                  <Button disabled={busy} onClick={() => setCreatingSpace(false)}>
                    Cancel
                  </Button>
                </Stack>
              </Stack>
            ) : (
              <Button disabled={busy} onClick={() => setCreatingSpace(true)}>
                + Create team space
              </Button>
            )}
          </Box>
        )}

        {label('Company library')}
        <Box
          sx={{
            bgcolor: tokens.surface,
            border: '1px solid',
            borderColor: alpha(tokens.accent, 0.16),
            borderRadius: '6px',
            p: 2,
          }}
        >
          {published ? (
            <>
              <Typography sx={{ color: tokens.accent, fontSize: 13, fontWeight: 700 }}>
                Published to the company
              </Typography>
              <Typography color="text.secondary" sx={{ fontSize: 11.5, lineHeight: 1.5, mt: 0.5 }}>
                Every employee can find and open this Lens. Withdrawing it leaves the Lens and its history untouched.
              </Typography>
              {canUnpublish && !publishDialog && (
                <Button
                  disabled={busy}
                  onClick={() => changeStatus('draft')}
                  size="small"
                  sx={{ mt: 1.5 }}
                  variant="outlined"
                >
                  Withdraw from company library
                </Button>
              )}
            </>
          ) : canPublish ? (
            <>
              <Typography sx={{ color: tokens.accent, fontSize: 13, fontWeight: 700 }}>
                Publish to the company
              </Typography>
              <Typography color="text.secondary" sx={{ fontSize: 11.5, lineHeight: 1.5, mt: 0.5 }}>
                Every employee will be able to find and open this Lens in the company library. Only its editors can
                change it.
              </Typography>
              {publishDialog && currentLens.spec.cards.length === 0 && (
                <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>
                  Add a card before publishing.
                </Typography>
              )}
              {publishDialog ? null : confirmingPublish ? (
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                  <Button disabled={busy} onClick={() => changeStatus('published')} size="small" variant="contained">
                    {setStatus.isPending ? 'Publishing…' : 'Yes, publish company-wide'}
                  </Button>
                  <Button disabled={busy} onClick={() => setConfirmingPublish(false)} size="small">
                    Cancel
                  </Button>
                </Stack>
              ) : (
                <Button
                  disabled={busy || currentLens.spec.cards.length === 0}
                  onClick={() => setConfirmingPublish(true)}
                  size="small"
                  sx={{ mt: 1.5 }}
                  variant="outlined"
                >
                  {currentLens.spec.cards.length === 0 ? 'Add a card before publishing' : 'Publish to company'}
                </Button>
              )}
            </>
          ) : (
            <>
              <Typography sx={{ color: tokens.accent, fontSize: 13, fontWeight: 700 }}>
                Company publication is an admin decision
              </Typography>
              <Typography color="text.secondary" sx={{ fontSize: 11.5, lineHeight: 1.5, mt: 0.5 }}>
                The company library is for reporting a whole company can rely on, so a reporting admin puts work there.
                Share this Lens by name, or give it a team home, and ask an admin when it is ready for everyone.
              </Typography>
            </>
          )}
        </Box>
        {currentView && (
          <FormControlLabel
            sx={{ mt: 2 }}
            control={
              <Checkbox
                checked={includeView}
                onChange={(_, checked) => {
                  setIncludeView(checked);
                  setCopied(false);
                }}
              />
            }
            label="Include current filters, dates and comparison"
          />
        )}
        {open && (
          <LensShareExport
            key={currentLens.id}
            lens={currentLens}
            url={shareUrl}
            view={sharedView}
            disabled={busy || detail.isError}
          />
        )}
      </Box>

      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{
          flexShrink: 0,
          bgcolor: tokens.surface,
          borderTop: '1px solid',
          borderColor: alpha(tokens.accent, 0.16),
          px: 3,
          py: 2,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Button
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(shareUrl);
              setCopied(true);
              setCopyError(false);
            } catch {
              setCopied(false);
              setCopyError(true);
            }
          }}
          startIcon={<LinkOutlinedIcon />}
          sx={{ color: tokens.accent, fontWeight: 700 }}
        >
          Copy link
        </Button>
        <Stack direction="row" spacing={1}>
          <Button disabled={busy} onClick={onClose} variant={publishDialog ? 'text' : 'contained'}>
            {publishDialog ? 'Cancel' : 'Done'}
          </Button>
          {publishedDialog && canUnpublish && (
            <Button disabled={busy} onClick={() => changeStatus('draft')} variant="outlined">
              {setStatus.isPending ? 'Unpublishing…' : 'Unpublish'}
            </Button>
          )}
          {publishedDialog && canShare && (
            <Button
              disabled={
                busy ||
                !recipientsLoaded ||
                creatingSpace ||
                (homeChanged &&
                  (spacesLoading || spacesError || (leavingSpace && !EMAIL_PATTERN.test(newManager.trim()))))
              }
              onClick={saveSharing}
              variant="contained"
            >
              {updateSharing.isPending ? 'Saving…' : 'Save'}
            </Button>
          )}
          {publishDialog && !published && canPublish && (
            <Button
              disabled={busy || currentLens.spec.cards.length === 0}
              onClick={() => changeStatus('published')}
              variant="contained"
            >
              {setStatus.isPending ? 'Publishing…' : 'Publish Lens'}
            </Button>
          )}
        </Stack>
      </Stack>
      {copied && (
        <Typography aria-live="polite" color="text.secondary" sx={{ fontSize: 11.5, px: 3, pb: 2 }}>
          {linkReach}
        </Typography>
      )}
      {copyError && (
        <Alert severity="error" sx={{ borderRadius: 0, mx: 3, mb: 2 }}>
          We couldn’t copy the link. Copy it manually: {shareUrl}
        </Alert>
      )}
    </Dialog>
  );
}
